import dayjs from 'dayjs'
import {
  EnrolledStatuses,
  HMOIntentOptions,
  CopayTypes,
  NHProviderCategories,
  planTypes,
  ProviderCategories,
  ProviderTiers,
  ProviderCategoriesMap
} from '../consts'
import { isNil } from 'lodash'
import $t from '../i18n'

const PASSIVE_EVENT_PROCESSOR_MODE = 'PASSIVE'

export const getIsHmoMember = userDetails => {
  const hmo_member_id = userDetails?.hmo_member_id
  return (
    !!hmo_member_id &&
    !isMemberCanceled(userDetails) &&
    !isMemberTermed(userDetails)
  )
}

export const isMemberInsuranceBeforePayment = userDetails => {
  const hmoStatus = userDetails?.hmo_status?.toLowerCase()
  return (
    !hmoStatus ||
    hmoStatus === EnrolledStatuses.ELECTED ||
    hmoStatus === EnrolledStatuses.PRE_MEMBER_FUTURE_EFFECTIVE_DATE ||
    hmoStatus === EnrolledStatuses.PRE_MEMBER_CURRENT_MONTH
  )
}

// The member has paid but Cognizant has not confirmed it yet. Only meaningful in
// PASSIVE mode; in ACTIVE the payment cards behave exactly as they did before S9.
export const isPaymentAwaitingConfirmation = userDetails =>
  userDetails?.event_processor_mode === PASSIVE_EVENT_PROCESSOR_MODE &&
  !!userDetails?.has_unconfirmed_payment

// The two payment cards are mutually exclusive: asking for payment while also
// confirming it would contradict itself in exactly the window S9 targets.
export const shouldShowPaymentReceivedNotification = userDetails =>
  getIsHmoMember(userDetails) &&
  !!userDetails?.isOwner &&
  isPaymentAwaitingConfirmation(userDetails)

// The payment summary "up to 48 hours" disclaimer only makes sense once Cognizant (not
// Antidote) is the one confirming payments. In ACTIVE mode Antidote approves payments
// directly, so the disclaimer would be misleading. Covers both the authenticated flow
// (userDetails) and the public/unauthenticated payment flow (unauthenticatedPaymentDetails).
export const shouldShowProcessingDisclaimer = (
  userDetails,
  unauthenticatedPaymentDetails
) =>
  (userDetails?.event_processor_mode ||
    unauthenticatedPaymentDetails?.event_processor_mode) ===
  PASSIVE_EVENT_PROCESSOR_MODE

export const shouldShowEnrollmentPaymentNotification = userDetails =>
  getIsHmoMember(userDetails) &&
  isMemberInsuranceBeforePayment(userDetails) &&
  !!userDetails?.isOwner &&
  !isPaymentAwaitingConfirmation(userDetails)

export const isMemberInsuranceActivated = userDetails => {
  const hmoStatus = userDetails?.hmo_status?.toLowerCase()
  return (
    hmoStatus === EnrolledStatuses.ENROLLED ||
    hmoStatus === EnrolledStatuses.DELINQUENT ||
    hmoStatus === EnrolledStatuses.SUSPENDED ||
    hmoStatus === EnrolledStatuses.TERMED_FUTURE
  )
}

export const isMemberInsuranceNotActivated = userDetails => {
  const hmoStatus = userDetails?.hmo_status?.toLowerCase()
  return (
    !hmoStatus ||
    hmoStatus === EnrolledStatuses.ELECTED ||
    hmoStatus === EnrolledStatuses.PRE_MEMBER_FUTURE_EFFECTIVE_DATE ||
    hmoStatus === EnrolledStatuses.PRE_MEMBER_CURRENT_MONTH ||
    hmoStatus === EnrolledStatuses.ENROLLED_FUTURE_EFFECTIVE_DATE
  )
}

export const isMemberCanceled = userDetails => {
  const hmoStatus = userDetails?.hmo_status?.toLowerCase()
  return hmoStatus === EnrolledStatuses.CANCELED
}

export const isMemberTermed = userDetails => {
  const hmoStatus = userDetails?.hmo_status?.toLowerCase()
  return hmoStatus === EnrolledStatuses.TERMED
}

export const isPastNhMember = userDetails => {
  return userDetails?.past_nh_member
}

export const isBotIntentAdministrativeOption = optionName => {
  return (
    optionName === HMOIntentOptions.MENTAL ||
    optionName === HMOIntentOptions.PRESCRIPTIONS ||
    optionName === HMOIntentOptions.MEDICAL_NOTE ||
    optionName === HMOIntentOptions.WELLNESS_VISIT ||
    optionName === HMOIntentOptions.REFERRALS
  )
}

export const isBotIntentChronic = intentOptions => {
  return !!intentOptions?.find(
    option =>
      // it should be includes and not equal because on the intent we are saving htnHMO and not htn
      // same for diabetes
      option?.name?.includes(HMOIntentOptions.HTN) ||
      option?.name?.includes(HMOIntentOptions.DIABETES)
  )
}

const skipPrimaryIntents = [
  HMOIntentOptions.PRESCRIPTIONS,
  HMOIntentOptions.REFERRALS,
  HMOIntentOptions.WELLNESS_VISIT
]

export const mapIntentToDefault = intentOptions => {
  if (
    intentOptions.filter(option => !skipPrimaryIntents.includes(option?.name))
      .length === 0
  ) {
    return false
  }

  return (
    intentOptions?.length > 0 &&
    !intentOptions?.filter(option => !option?.is_administrative)?.length
  )
}

export const getBotTypesByIntent = intentOptions => {
  if (!intentOptions || !intentOptions.length) return []

  // array without the mental and administrative options
  const intentOptionsWithoutAdministrative = intentOptions?.filter(
    option => !option?.is_administrative
  )

  return intentOptionsWithoutAdministrative?.map(option => option?.name)
}

export const getCopayAndNetworkCopay = ({ planDetailsCopay, provider }) => {
  const networkCopay = getNetworkCopayAccordingToProvider(
    planDetailsCopay,
    provider
  )
  const copay = provider?.is_antidote
    ? provider?.copay || getAntidoteProviderCopay(planDetailsCopay, provider)
    : networkCopay?.copay

  return {
    copay,
    networkCopay
  }
}

export const getTextAfterCopay = ({
  planDetailsCopay,
  provider,
  useDefaultText = true
}) => {
  const isCatastrophicPlan =
    planDetailsCopay?.template_type?.includes('catastrophic')
  if (isCatastrophicPlan) {
    switch (provider?.specialities?.[0]?.category) {
      case ProviderCategoriesMap.pcp:
        return (
          (useDefaultText ? $t('providerPage.providerDetails.copay') : '') +
          ' ' +
          $t('providerPage.providerDetails.forTheFirst3Visits')
        )
      case ProviderCategoriesMap.specialist:
        return $t('providerPage.providerDetails.coinsuranceAfterDeductible')
      case ProviderCategoriesMap.mental:
        return $t('providerPage.providerDetails.coinsuranceAfterDeductible')
      default:
        return useDefaultText ? $t('providerPage.providerDetails.copay') : ''
    }
  }
  return useDefaultText ? $t('providerPage.providerDetails.copay') : ''
}

export const getAntidoteProviderCopay = (planInfo, provider) => {
  const providerSpecialtyCategory = determineSpecialtyCategory(provider)
  return formatPrice(planInfo?.[providerSpecialtyCategory]?.antidote_provider)
}
const determineSpecialtyCategory = provider => {
  let providerSpecialtyCategory =
    ProviderCategories?.[provider?.specialities?.[0]?.category]

  if (!providerSpecialtyCategory) {
    providerSpecialtyCategory = provider?.pcp
      ? 'pcp'
      : provider?.provider_type == 'individual'
      ? 'specialist'
      : null
  }

  return providerSpecialtyCategory
}

const containsNumber = str => /\d/.test(str)

export const formatPrice = price => {
  if (typeof price === 'number') {
    return price
  }
  return price && containsNumber(price)
    ? parseInt(price.trim().replace('$', ''))
    : null
}

export const getNetworkCopayAccordingToProvider = (planInfo, provider) => {
  const providerSpecialtyCategory = determineSpecialtyCategory(provider)
  const isCatastrophicPlan = planInfo?.template_type?.includes('catastrophic')

  if (isCatastrophicPlan) {
    return {
      copay: 0,
      isCopayMinRate: false,
      copayMaxRate: null
    }
  }

  // only tier 1 and tier 2 (antidote tier is not network copay)
  const providerTier =
    ProviderTiers?.[provider?.tier] || ProviderTiers['Tier 1']

  const copayWithReferralRate = formatPrice(
    planInfo?.[providerSpecialtyCategory]?.antidote_referral
  )

  const copayTierRate = formatPrice(
    planInfo?.[providerSpecialtyCategory]?.[providerTier]
  )

  return {
    copay:
      // show copay with refferal rate only if it is lower than copay tier rate
      // if copay with refferal rate is null - show copay tier rate
      // if copay tier rate is null - show copay with refferal rate
      // if both are null - show null
      // note we are checking if the value is not undefined or null because it can be 0 and its valid value
      isNil(copayWithReferralRate)
        ? copayTierRate
        : isNil(copayTierRate)
        ? copayWithReferralRate
        : copayWithReferralRate <= copayTierRate
        ? copayWithReferralRate
        : copayTierRate,
    isCopayMinRate: copayWithReferralRate < copayTierRate,
    copayMaxRate: copayTierRate
  }
}

// TODO: !! remove NH network copay after removing Nirvana
export const getNHNetworkCopay = memberInfo => {
  const { columnHeaders, rowHeaders, plan } =
    memberInfo?.planInfo?.costShareInfo || {}
  // relevant fields 'white AH referral', 'QCP'
  // Nirvana sending the data as colums, rows and values (as table)
  const copayWithReferralIndex = columnHeaders?.findIndex(
    column => column === CopayTypes.REFERRAL
  )
  const copayQCPIndex = columnHeaders?.findIndex(
    column => column === CopayTypes.QCP
  )
  // remove $ sign and convert to number
  try {
    const copayWithReferralRate = plan?.values?.map(row =>
      parseInt(row?.[copayWithReferralIndex]?.replace('$', ''))
    )
    const copayQCPRate = plan?.values?.map(row =>
      parseInt(row?.[copayQCPIndex]?.replace('$', ''))
    )

    // get object category (provider specialty) that will indicate the copay according to copay type( with refferal or qcp(tier1/2))
    const networkCopay = {}
    rowHeaders?.forEach((category, i) => {
      const categoryObject = {
        [NHProviderCategories[category]]: {
          antidote_referral: copayWithReferralRate[i],
          antidote_tier_1: copayQCPRate[i],
          antidote_tier_2: copayQCPRate[i]
        }
      }
      Object.assign(networkCopay, categoryObject)
    })
    // the copay is according to state. currently we are getting one state
    // we return object with state key in order to ensure that the copay will be according to state
    // we won't show copay in other state for provider that is not in this state
    return networkCopay
  } catch (e) {
    return {}
  }
}

export const isNeedToAskForHmoPolicyConsent = consents => {
  // check if there is hmo policy consent is the list
  // if exist check the date time (we should raise the consent every year)

  let arrayForSort = [...consents]
  const sortConcentstByDate = arrayForSort?.sort((a, b) => {
    return new Date(b?.date) - new Date(a?.date)
  })

  // first element that is hmo policy consent (will be the newest one because we sorted by date)
  const hmoPolicyConcent = sortConcentstByDate?.find(
    consent => consent?.file_type === 'oh_consent'
  )

  const now = dayjs().utc()

  // if there is no hmo policy consent - we need to ask for it
  // if the hmo policy consent is older than a year - we need to ask for it
  return (
    !hmoPolicyConcent || now.diff(hmoPolicyConcent?.creationDate, 'year') > 0
  )
}

// if the user is an hmo member and the insurance is not activated
// and he selected one time payment - then the flow shouldn't be hmo payment
export const isHmoPaymentFlow = (userDetails, selectedPlan) => {
  const isHmoMember = getIsHmoMember(userDetails)
  const isInsuranceInactive = isMemberInsuranceNotActivated(userDetails)
  const isOneTimePlan =
    isInsuranceInactive && selectedPlan?.type === planTypes.ONE_TIME

  return isHmoMember && !isOneTimePlan
}
